import * as React from 'react';

import classNames from 'classnames';

import type {
    GetQuoteById_quote as QuoteType,
    GetInvoiceById_invoice as InvoiceType,
    SalesDocumentUpdateInput,
    QuoteProductsUpdateInput,
    QuoteSubtotalItemsUpdateInput,
    GetUserNames_users as UsersConnection,
    GetPossibleProducts_quotePossibleProducts as QuotePossibleProducts,
} from 'nutshell-graphql-types';

import {LoadingPage} from '../loading-page/loading-page';
import {SectionPlaceholder} from '../placeholder/section-placeholder';
import {TablePlaceholder} from '../placeholder/table-placeholder';

import {
    PREPARED_FOR,
    QUOTE_DETAILS,
    NOTES,
    SUBTOTAL_TABLE_TEMPLATE,
    SUBTOTAL_TABLE,
    FOOTER,
    PRODUCT_TABLE,
    PRODUCT_TABLE_TEMPLATE,
    HEADER,
    getIsSectionImmutable,
    PAYMENT_INFO,
} from './helpers';
import type {SectionName} from './helpers';
import {InvoiceTemplateDetails} from './invoice-template-details';
import {InvoicePaymentInfoWrapper} from './wrappers/invoice-payment-info-wrapper';
import {QuoteFooterWrapper} from './wrappers/quote-footer-wrapper';
import {QuoteHeaderWrapper} from './wrappers/quote-header-wrapper';
import {QuoteNoteWrapper} from './wrappers/quote-note-wrapper';
import {QuotePreparedByWrapper} from './wrappers/quote-prepared-by-wrapper';
import {QuotePreparedForWrapper} from './wrappers/quote-prepared-for-wrapper';
import {QuoteProductTableWrapper} from './wrappers/quote-product-table-wrapper';
import {QuoteSubtotalWrapper} from './wrappers/quote-subtotal-wrapper';

import styles from './quote.module.css';

type Props = {
    quote: QuoteType | InvoiceType;
    handleSaveQuote: (quote: SalesDocumentUpdateInput) => void;
    handleSaveProductTable: (quote: QuoteProductsUpdateInput) => void;
    handleSaveSubtotalTable: (quote: QuoteSubtotalItemsUpdateInput) => void;
    isTemplate: boolean;
    users: UsersConnection | null | undefined;
    isLoading: boolean;
    quotePossibleProductsEdges: QuotePossibleProducts;
    toggleHasActiveSection: (isActive: boolean) => void;
};

const SMALL_SCREEN_WIDTH = 1030;

export function Quote(props: Props) {
    const [activeSection, setActiveSection] = React.useState<SectionName | null>(null);
    const [sectionToSave, setSectionToSave] = React.useState<SectionName | null>(null);
    const [isSmallScreen, setIsSmallScreen] = React.useState<boolean>(
        window.innerWidth < SMALL_SCREEN_WIDTH
    );

    const isImmutable = getIsSectionImmutable(props.quote.displayStatus);

    const handleToggleActive = (section: SectionName) => {
        if (activeSection === section) {
            setActiveSection(null);
            props.toggleHasActiveSection(false);
        } else if (!activeSection) {
            setActiveSection(section);
            props.toggleHasActiveSection(true);
        } else {
            setSectionToSave(activeSection);
            setActiveSection(section);
        }
    };

    React.useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < SMALL_SCREEN_WIDTH);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    React.useEffect(() => {
        setActiveSection(null);
    }, [props.quote.displayStatus]);

    const handleCancel = () => {
        setActiveSection(null);
        props.toggleHasActiveSection(false);
    };

    const handleSave = () => {
        if (!sectionToSave) {
            setActiveSection(null);
            props.toggleHasActiveSection(false);
        }
        setSectionToSave(null);
    };

    const handleQuoteSave = (quote: SalesDocumentUpdateInput) => {
        handleSave();
        props.handleSaveQuote(quote);
    };

    const handleProductTableSave = (quote: QuoteProductsUpdateInput) => {
        handleSave();
        props.handleSaveProductTable(quote);
    };

    const handleSaveSubtotalTable = (quote: QuoteSubtotalItemsUpdateInput) => {
        handleSave();
        props.handleSaveSubtotalTable(quote);
    };

    if (props.isLoading) {
        return (
            <div className='absolutely-center-and-fill'>
                <LoadingPage />
            </div>
        );
    }

    const preparedByInvoiceProps =
        props.quote.__typename === 'Invoice'
            ? {
                  purchaseOrderNumber: props.quote.purchaseOrderNumber,
                  jobNumber: props.quote.jobNumber,
                  customerNumber: props.quote.customerNumber,
                  referenceNumber: props.quote.referenceNumber,
              }
            : undefined;

    const headerStyleName = classNames({
        [styles['header-container-template']]: props.isTemplate,
        [styles['header-container']]: !props.isTemplate,
    });

    return (
        <div className='flex flex-dir-col gap-16'>
            <div className={headerStyleName}>
                <QuoteHeaderWrapper
                    type={props.quote.__typename}
                    isActive={activeSection === HEADER}
                    onCancel={handleCancel}
                    onSave={handleQuoteSave}
                    id={props.quote.id}
                    headerLeftColumn={props.quote.headerLeftColumn}
                    headerRightColumn={props.quote.headerRightColumn}
                    logo={props.quote.logo}
                    forceSave={sectionToSave === HEADER}
                    // @ts-expect-error
                    onToggleActive={handleToggleActive}
                    isTemplate={props.isTemplate}
                    isImmutable={isImmutable}
                />
            </div>
            <div className={styles['prepared-container']}>
                <div className={styles['half-section']}>
                    {props.isTemplate ? (
                        <SectionPlaceholder sectionName={PREPARED_FOR} numRows={4} rowHeight={20} />
                    ) : (
                        <QuotePreparedForWrapper
                            isActive={activeSection === PREPARED_FOR}
                            onCancel={handleCancel}
                            onSave={handleQuoteSave}
                            id={props.quote.id}
                            preparedFor={props.quote.preparedFor}
                            // @ts-expect-error
                            onToggleActive={handleToggleActive}
                            forceSave={sectionToSave === PREPARED_FOR}
                            isImmutable={isImmutable}
                        />
                    )}
                </div>
                <div className={styles['half-section']}>
                    {props.isTemplate && props.quote.__typename === 'Invoice' && (
                        <InvoiceTemplateDetails
                            quote={props.quote}
                            isActive={activeSection === QUOTE_DETAILS}
                            onCancel={handleCancel}
                            onSave={handleQuoteSave}
                            // @ts-expect-error
                            onToggleActive={handleToggleActive}
                            forceSave={sectionToSave === QUOTE_DETAILS}
                        />
                    )}
                    {props.isTemplate && props.quote.__typename === 'Quote' && (
                        <SectionPlaceholder
                            sectionName={QUOTE_DETAILS}
                            numRows={4}
                            rowHeight={20}
                        />
                    )}
                    {!props.isTemplate && props.users && (
                        <QuotePreparedByWrapper
                            type={props.quote.__typename}
                            users={props.users}
                            isActive={activeSection === QUOTE_DETAILS}
                            id={props.quote.id}
                            preparedBy={props.quote.preparedBy}
                            preparedTime={props.quote.preparedTime}
                            validUntilDate={props.quote.validUntilDate}
                            displayNumber={props.quote.displayNumber}
                            preparedTimeFormatted={props.quote.preparedTimeFormatted}
                            validUntilDateFormatted={props.quote.validUntilDateFormatted}
                            // @ts-expect-error
                            onToggleActive={handleToggleActive}
                            onCancel={handleCancel}
                            onSave={handleQuoteSave}
                            forceSave={sectionToSave === QUOTE_DETAILS}
                            isImmutable={isImmutable}
                            {...preparedByInvoiceProps}
                        />
                    )}
                </div>
            </div>
            <div className='full-width'>
                <QuoteProductTableWrapper
                    isActive={
                        props.isTemplate
                            ? activeSection === PRODUCT_TABLE_TEMPLATE
                            : activeSection === PRODUCT_TABLE
                    }
                    onCancel={handleCancel}
                    onSave={handleProductTableSave}
                    forceSave={
                        props.isTemplate
                            ? sectionToSave === PRODUCT_TABLE_TEMPLATE
                            : sectionToSave === PRODUCT_TABLE
                    }
                    id={props.quote.id}
                    isSkuShown={props.quote.isSkuShown}
                    isTermShown={props.quote.isTermShown}
                    isDiscountShown={props.quote.isDiscountShown}
                    productLineItems={props.quote.productLineItems}
                    total={props.quote.total}
                    // @ts-expect-error
                    onToggleActive={handleToggleActive}
                    quotePossibleProductsEdges={props.quotePossibleProductsEdges}
                    isTemplate={props.isTemplate}
                    isSmallScreen={isSmallScreen}
                    isImmutable={isImmutable}
                    type={props.quote.__typename}
                />
            </div>
            <div className={styles['body-subtotal-container']}>
                <div className={styles['notes-container']}>
                    <QuoteNoteWrapper
                        isActive={activeSection === NOTES}
                        onCancel={handleCancel}
                        onSave={handleQuoteSave}
                        id={props.quote.id}
                        body={props.quote.body}
                        // @ts-expect-error
                        onToggleActive={handleToggleActive}
                        forceSave={sectionToSave === NOTES}
                        isTemplate={props.isTemplate}
                        isImmutable={isImmutable}
                    />
                </div>
                <div className={styles['pricing-container']}>
                    {props.isTemplate ? (
                        <TablePlaceholder sectionName={SUBTOTAL_TABLE_TEMPLATE} numRows={4} />
                    ) : (
                        <QuoteSubtotalWrapper
                            isActive={activeSection === SUBTOTAL_TABLE}
                            onCancel={handleCancel}
                            onSave={handleSaveSubtotalTable}
                            id={props.quote.id}
                            subtotalLineItems={props.quote.subtotalLineItems}
                            subtotal={props.quote.subtotal}
                            total={props.quote.total}
                            // @ts-expect-error
                            onToggleActive={handleToggleActive}
                            forceSave={sectionToSave === SUBTOTAL_TABLE}
                            isImmutable={isImmutable}
                        />
                    )}
                </div>
            </div>
            {props.quote.__typename === 'Invoice' && (
                <InvoicePaymentInfoWrapper
                    id={props.quote.id}
                    onSave={handleQuoteSave}
                    // @ts-expect-error
                    isActive={activeSection === PAYMENT_INFO}
                    onCancel={handleCancel}
                    paymentInfo={props.quote.paymentAndTerms}
                    // @ts-expect-error
                    onToggleActive={handleToggleActive}
                    isTemplate={props.isTemplate}
                    isImmutable={isImmutable}
                    // @ts-expect-error
                    forceSave={sectionToSave === PAYMENT_INFO}
                />
            )}
            <div className={styles['terms-container']}>
                <QuoteFooterWrapper
                    isActive={activeSection === FOOTER}
                    onCancel={handleCancel}
                    onSave={handleQuoteSave}
                    id={props.quote.id}
                    footer={props.quote.footer}
                    // @ts-expect-error
                    onToggleActive={handleToggleActive}
                    forceSave={sectionToSave === FOOTER}
                    isTemplate={props.isTemplate}
                    isImmutable={isImmutable}
                />
            </div>
        </div>
    );
}
